import * as React from "react"
import { cn } from "@/lib/utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { TrendingUp, TrendingDown, Minus } from "lucide-react"

const StatsCard = ({
  title,
  value,
  description,
  icon: Icon,
  trend,
  trendValue,
  trendLabel,
  status,
  accent,
  className,
}) => {
  const trendConfig = {
    up: {
      icon: TrendingUp,
      color: "text-[hsl(var(--status-success))]",
      bg: "bg-[hsl(var(--status-success-bg))]",
    },
    down: {
      icon: TrendingDown,
      color: "text-[hsl(var(--status-error))]",
      bg: "bg-[hsl(var(--status-error-bg))]",
    },
    neutral: {
      icon: Minus,
      color: "text-[hsl(var(--text-muted))]",
      bg: "bg-[hsl(var(--bg-hover))]",
    },
  }

  const trendInfo = trend ? trendConfig[trend] : null
  const TrendIcon = trendInfo?.icon

  const accentColors = {
    brand: "bg-[hsl(var(--brand-50))] text-[hsl(var(--brand-500))]",
    success: "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]",
    warning: "bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]",
    error: "bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))]",
    info: "bg-[hsl(var(--status-info-bg))] text-[hsl(var(--status-info))]",
  }

  return (
    <Card className={cn("group transition-shadow hover:shadow-[var(--shadow-md)]", className)}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <CardTitle className="text-[13px] font-medium text-[hsl(var(--text-secondary))] leading-snug">
          {title}
        </CardTitle>
        {Icon && (
          <div
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
              accent ? accentColors[accent] : "bg-[hsl(var(--bg-hover))] text-[hsl(var(--text-muted))]"
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[28px] font-bold tracking-tight text-[hsl(var(--text-primary))]">
            {value}
          </span>
        </div>
        {(description || trend) && (
          <div className="mt-2 flex items-center gap-2">
            {trendInfo && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                  trendInfo.bg,
                  trendInfo.color
                )}
              >
                <TrendIcon className="h-3 w-3" />
                {trendValue}
              </span>
            )}
            {trendLabel && (
              <span className="text-[12px] text-[hsl(var(--text-muted))]">
                {trendLabel}
              </span>
            )}
          </div>
        )}
        {!trend && description && (
          <p className="mt-1 text-[12px] text-[hsl(var(--text-muted))]">
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function StatsCardSkeleton({ className }) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div className="h-4 w-24 animate-pulse rounded bg-[hsl(var(--bg-hover))]" />
        <div className="h-8 w-8 animate-pulse rounded-[var(--radius-md)] bg-[hsl(var(--bg-hover))]" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-8 w-20 animate-pulse rounded bg-[hsl(var(--bg-hover))]" />
        <div className="mt-2 h-3 w-32 animate-pulse rounded bg-[hsl(var(--bg-hover))]" />
      </CardContent>
    </Card>
  )
}

export { StatsCard, StatsCardSkeleton }
